import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { productAPI } from '../../api/product.api';
import { carouselAPI } from '../../api/carousel.api';
import { useCartWishlist } from '../../context/CartWishlistContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import ProductCard from '../../components/product/ProductCard';
import HeroBanner from '../../components/hero/HeroBanner';
import Loader from '../../components/common/Loader';
import { HOME_CATEGORY_PRODUCT_LIMIT, prioritizeHomepageProducts } from '../../utils/homeProducts';
import { imageLooksUsable, normalizeImageUrl } from '../../utils/productImage';
import './Home.css';

const BRAND_LOGO_IMAGES = {
  Chanel: '/images/brands/chanel.png',
  Prada: '/images/brands/prada.png',
  Rolex: '/images/brands/rolex.png',
  Vogue: '/images/brands/vogue.png',
  Zippo: '/images/brands/zippo.png',
};

const SECTION_CONFIG = [
  {
    key: 'watches',
    brands: ['JAEGER-LECOULTRE', { name: 'ROLEX', logo: BRAND_LOGO_IMAGES.Rolex }, 'OMEGA'],
    fallback: [
      { id: 'watch-1', title: 'Watch 9', price: 199, category: 'watches', image: 'https://images.unsplash.com/photo-1523170335258-f5ed11844a49?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-01T10:00:00.000Z' },
      { id: 'watch-2', title: 'Watch 91', price: 199, category: 'watches', image: 'https://images.unsplash.com/photo-1547996160-81dfa63595aa?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-02T10:00:00.000Z' },
      { id: 'watch-3', title: 'Watch 923', price: 199, category: 'watches', image: 'https://images.unsplash.com/photo-1524805444758-089113d48a6d?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-03T10:00:00.000Z' },
      { id: 'watch-4', title: 'Watch 997', price: 199, category: 'watches', image: 'https://images.unsplash.com/photo-1612817159949-195b6eb9e31a?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-04T10:00:00.000Z' }
    ]
  },
  {
    key: 'eyewear',
    brands: ['GUCCI', { name: 'VOGUE', logo: BRAND_LOGO_IMAGES.Vogue }, 'Persol', { name: 'PRADA', logo: BRAND_LOGO_IMAGES.Prada }],
    fallback: [
      { id: 'eyewear-1', title: 'Sunglasses 113', price: 199, category: 'eyewear', image: 'https://images.unsplash.com/photo-1577803645773-f96470509666?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-01T10:00:00.000Z' },
      { id: 'eyewear-2', title: 'Sunglasses 282', price: 199, category: 'eyewear', image: 'https://images.unsplash.com/photo-1511499767150-a48a237f0083?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-02T10:00:00.000Z' },
      { id: 'eyewear-3', title: 'Sunglasses 828', price: 199, category: 'eyewear', image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-03T10:00:00.000Z' },
      { id: 'eyewear-4', title: 'Optics 992', price: 199, category: 'eyewear', image: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-04T10:00:00.000Z' }
    ]
  },
  {
    key: 'eyeglasses',
    brands: ['Ray-Ban', 'Oakley', 'Persol'],
    fallback: [
      { id: 'eyeglasses-1', title: 'Optical Frame 110', price: 199, category: 'eyeglasses', image: 'https://images.unsplash.com/photo-1591076482161-42ce6da69f67?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-01T10:00:00.000Z' },
      { id: 'eyeglasses-2', title: 'Classic Frame 282', price: 199, category: 'eyeglasses', image: 'https://images.unsplash.com/photo-1574258495973-f010dfbb5371?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-02T10:00:00.000Z' },
      { id: 'eyeglasses-3', title: 'Daily Frame 828', price: 199, category: 'eyeglasses', image: 'https://images.unsplash.com/photo-1509695507497-903c140c43b0?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-03T10:00:00.000Z' },
      { id: 'eyeglasses-4', title: 'Premium Frame 992', price: 199, category: 'eyeglasses', image: 'https://images.unsplash.com/photo-1574258495973-f010dfbb5371?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-04T10:00:00.000Z' }
    ]
  },
  {
    key: 'perfume',
    brands: [{ name: 'Chanel', logo: BRAND_LOGO_IMAGES.Chanel }, 'GIORGIO ARMANI', 'VERSACE', 'ck'],
    fallback: [
      { id: 'perfume-1', title: 'Bleu de Chanel', price: 199, category: 'perfume', image: 'https://images.unsplash.com/photo-1594035910387-fea47794261f?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-01T10:00:00.000Z' },
      { id: 'perfume-2', title: 'Coco Mademoiselle', price: 199, category: 'perfume', image: 'https://images.unsplash.com/photo-1592945403244-b3fbafd7f539?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-02T10:00:00.000Z' },
      { id: 'perfume-3', title: 'Chanel Gabrielle', price: 199, category: 'perfume', image: 'https://images.unsplash.com/photo-1615634262417-d5310b5e7d0f?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-03T10:00:00.000Z' },
      { id: 'perfume-4', title: 'Chanel No. 5', price: 199, category: 'perfume', image: 'https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-04T10:00:00.000Z' }
    ]
  },
  {
    key: 'knives',
    brands: ['Benchmade', 'Spyderco', 'CRKT'],
    fallback: [
      { id: 'knife-1', title: 'Knife MK11', price: 199, category: 'knives', image: 'https://images.unsplash.com/photo-1593618998160-e34014e67546?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-01T10:00:00.000Z' },
      { id: 'knife-2', title: 'Knife SW26', price: 199, category: 'knives', image: 'https://images.unsplash.com/photo-1590156527196-f9d7cdd3f5e7?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-02T10:00:00.000Z' },
      { id: 'knife-3', title: 'Knife KZ17', price: 199, category: 'knives', image: 'https://images.unsplash.com/photo-1588444650733-d53fdbf6d0bf?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-03T10:00:00.000Z' },
      { id: 'knife-4', title: 'Knife GR85', price: 199, category: 'knives', image: 'https://images.unsplash.com/photo-1588444650904-13023ecb609f?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-04T10:00:00.000Z' }
    ]
  },
  {
    key: 'presents',
    brands: ['Gift Sets', 'Accessories', { name: 'Zippo', logo: BRAND_LOGO_IMAGES.Zippo }, 'Special Picks'],
    fallback: [
      { id: 'present-1', title: 'Gift Set HC11', price: 199, category: 'presents', image: 'https://images.unsplash.com/photo-1512909006721-3d6018887383?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-01T10:00:00.000Z' },
      { id: 'present-2', title: 'Special Gift WX26', price: 199, category: 'presents', image: 'https://images.unsplash.com/photo-1513201099705-a9746e1e201f?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-02T10:00:00.000Z' },
      { id: 'present-3', title: 'Surprise Box KZ17', price: 199, category: 'presents', image: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-03T10:00:00.000Z' },
      { id: 'present-4', title: 'Premium Gift GR85', price: 199, category: 'presents', image: 'https://images.unsplash.com/photo-1528842726275-4ca3008124da?auto=format&fit=crop&w=900&q=80', createdAt: '2026-04-04T10:00:00.000Z' }
    ]
  }
];

const LIMITED_OFFERS = [
  { id: 'watch', titleKey: 'home.offer.watchTitle', eyebrowKey: 'home.offer.watchEyebrow', href: '/products?category=watches', variant: 'watch', salePercentage: 15 },
  { id: 'sale', titleKey: 'home.offer.saleTitle', eyebrowKey: 'home.offer.saleEyebrow', href: '/products', variant: 'christmas', salePercentage: 20 },
  { id: 'gift', titleKey: 'home.offer.giftTitle', descriptionKey: 'home.offer.giftDescription', href: '/products', variant: 'gift', salePercentage: 0 }
];

const BRAND_LOGOS = [
  { name: 'Ray-Ban', brand: 'Ray-Ban', style: 'ray-ban' },
  { name: 'Victorinox', brand: 'Victorinox', style: 'victorinox' },
  { name: 'Rolex', brand: 'Rolex', style: 'rolex', logo: BRAND_LOGO_IMAGES.Rolex },
  { name: 'Omega', brand: 'Omega', style: 'omega' },
  { name: 'Chanel', brand: 'Chanel', style: 'chanel', logo: BRAND_LOGO_IMAGES.Chanel },
  { name: 'Gucci', brand: 'Gucci', style: 'gucci' },
  { name: 'Prada', brand: 'Prada', style: 'prada', logo: BRAND_LOGO_IMAGES.Prada },
  { name: 'Persol', brand: 'Persol', style: 'persol' },
  { name: 'Vogue', brand: 'Vogue', style: 'vogue', logo: BRAND_LOGO_IMAGES.Vogue },
  { name: 'Versace', brand: 'Versace', style: 'versace' },
  { name: 'Givenchy', brand: 'Givenchy', style: 'givenchy' },
  { name: 'Zippo', brand: 'Zippo', style: 'zippo', logo: BRAND_LOGO_IMAGES.Zippo },
];

const WHY_ITEMS = [
  { titleKey: 'home.why.serviceTitle', textKey: 'home.why.serviceText', icon: 'service' },
  { titleKey: 'home.why.deliveryTitle', textKey: 'home.why.deliveryText', icon: 'delivery' },
  { titleKey: 'home.why.safetyTitle', textKey: 'home.why.safetyText', icon: 'safety' }
];

const TESTIMONIALS = [
  {
    quote: 'საათი ფასდაკლების დროს შევუკვეთე და სწრაფად მომივიდა. შეფუთვა პრემიუმ კლასის იყო და საათი ფოტოებზეც კი უკეთესად გამოიყურებოდა.',
    author: 'ლადო გოცირიძე',
    location: 'თბილისი, საქართველო',
    rating: 5
  },
  {
    quote: 'მზის სათვალე ძალიან კომფორტულია და ხარისხი შესანიშნავია. შეკვეთა მარტივი იყო, მიწოდება სწრაფი და ყველაფერი იდეალურ მდგომარეობაში მოვიდა.',
    author: 'გიორგი ვაჩეიშვილი',
    location: 'თბილისი, საქართველო',
    rating: 5
  },
  {
    quote: 'საჩუქარი ვიყიდე და მომსახურება თავიდან ბოლომდე შეუფერხებელი იყო.',
    author: 'კახა რამიშვილი',
    location: 'თბილისი, საქართველო',
    rating: 5
  }
];

const isValidImage = (value) => typeof value === 'string' && /^https?:\/\//i.test(value) && !value.includes('example.com');
const getProductId = (product) => String(product.id || product._id || '');
const getBrandProductsUrl = (brand) => `/products?brand=${encodeURIComponent(brand)}`;
const getBrandName = (brand) => (typeof brand === 'string' ? brand : brand?.name || brand?.brand || '');
const getBrandLogo = (brand) => (typeof brand === 'string' ? '' : brand?.logo || brand?.image || brand?.imageUrl || '');
const getCarouselSlideImage = (slide) => normalizeImageUrl(slide?.imageUrl || slide?.image || slide?.logo || '');
const getCarouselSlideHref = (slide) => {
  if (slide?.href) {
    return slide.href;
  }

  if (slide?.brand) {
    return getBrandProductsUrl(slide.brand);
  }

  return '/products';
};
const getCarouselSlideButtons = (slide = {}) => {
  if (slide.showButton === false) {
    return [];
  }

  const buttons = Array.isArray(slide.buttons)
    ? slide.buttons
      .map((button) => ({
        label: String(button.label || button.text || button.cta || '').trim(),
        href: String(button.href || button.link || '/products').trim() || '/products',
      }))
      .filter((button) => button.label)
    : [];

  if (buttons.length) {
    return buttons;
  }

  return slide.cta ? [{ label: slide.cta, href: getCarouselSlideHref(slide) }] : [];
};
const splitLines = (value) => String(value || '').split('\n').filter((line) => line.trim());
const SALE_QUERY_KEYS = ['salePercentage', 'sale', 'discount'];

const buildPromoHref = (href, salePercentage) => {
  const target = href || '/products';

  if (!salePercentage) {
    return target;
  }

  const baseOrigin = 'https://sifrifana.local';
  const url = new URL(target, baseOrigin);
  const hasSaleParam = SALE_QUERY_KEYS.some((key) => url.searchParams.has(key));

  if (!hasSaleParam) {
    url.searchParams.set('salePercentage', String(salePercentage));
  }

  return url.origin === baseOrigin
    ? `${url.pathname}${url.search}${url.hash}`
    : url.toString();
};

const normalizeProduct = (product, fallbackCategory) => ({
  ...product,
  id: product.id || product._id || `${fallbackCategory}-${product.title || product.name}`,
  title: product.title || product.name || '',
  category: product.category || fallbackCategory,
  image: isValidImage(product.image)
    ? product.image
    : isValidImage(product.images?.[0])
      ? product.images[0]
      : null
});

const normalizeCarouselSlide = (slide = {}) => {
  const title = slide.title || slide.name || slide.brand || '';
  const imageUrl = getCarouselSlideImage(slide);
  const buttons = getCarouselSlideButtons(slide);

  return {
    id: slide.id || `${title}-${slide.href || slide.brand || 'carousel'}`,
    title,
    subtitle: slide.subtitle || '',
    cta: buttons[0]?.label || '',
    href: buttons[0]?.href || getCarouselSlideHref(slide),
    buttons,
    imageUrl: imageLooksUsable(imageUrl) ? imageUrl : '',
    style: slide.style || String(title || 'custom').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'custom',
  };
};

const BrandStrip = ({ brands = [], label }) => {
  const items = brands
    .map((brand) => ({
      name: getBrandName(brand),
      logo: getBrandLogo(brand),
    }))
    .filter((brand) => brand.name);

  if (!items.length) {
    return null;
  }

  const renderItems = (hidden = false) => items.map((brand) => {
    const logoUrl = imageLooksUsable(brand.logo) ? normalizeImageUrl(brand.logo) : '';

    return (
      <span className="section-brand-marquee__item" key={`${brand.name}-${hidden ? 'copy' : 'primary'}`} aria-hidden={hidden}>
        {logoUrl ? <img src={logoUrl} alt={hidden ? '' : brand.name} /> : <span>{brand.name}</span>}
      </span>
    );
  });

  return (
    <div className="section-brand-marquee" aria-label={label}>
      <div className="section-brand-marquee__track">
        <div className="section-brand-marquee__group">{renderItems()}</div>
        <div className="section-brand-marquee__group" aria-hidden="true">{renderItems(true)}</div>
      </div>
    </div>
  );
};

const CategoryProductCarousel = ({ products, label, onAddToCart, onAddToWishlist }) => {
  const viewportRef = React.useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateControls = React.useCallback(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    setCanScrollPrev(viewport.scrollLeft > 4);
    setCanScrollNext(viewport.scrollLeft < maxScrollLeft - 4);
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return undefined;
    }

    viewport.scrollTo({ left: 0, behavior: 'auto' });
    const frame = window.requestAnimationFrame(updateControls);
    viewport.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls);

    return () => {
      window.cancelAnimationFrame(frame);
      viewport.removeEventListener('scroll', updateControls);
      window.removeEventListener('resize', updateControls);
    };
  }, [products, updateControls]);

  const scrollProducts = (direction) => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const rail = viewport.querySelector('.home-product-carousel__rail');
    const cards = Array.from(viewport.querySelectorAll('.product-card'));

    if (rail && cards.length > 0 && window.matchMedia('(max-width: 640px)').matches) {
      const centerOffset = Math.max(0, (viewport.clientWidth - cards[0].offsetWidth) / 2);
      const cardOffsets = cards.map((card) => Math.max(
        0,
        card.offsetLeft - rail.offsetLeft - centerOffset
      ));
      const currentIndex = cardOffsets.reduce((closestIndex, offset, index) => (
        Math.abs(offset - viewport.scrollLeft) < Math.abs(cardOffsets[closestIndex] - viewport.scrollLeft)
          ? index
          : closestIndex
      ), 0);
      const nextIndex = Math.min(cards.length - 1, Math.max(0, currentIndex + direction));

      viewport.scrollTo({
        left: cardOffsets[nextIndex],
        behavior: 'smooth',
      });
      return;
    }

    viewport.scrollBy({
      left: direction * Math.max(1, viewport.clientWidth - 24),
      behavior: 'smooth',
    });
  };

  return (
    <div className="home-product-carousel" aria-label={label}>
      <button
        type="button"
        className="home-product-carousel__arrow home-product-carousel__arrow--prev"
        onClick={() => scrollProducts(-1)}
        disabled={!canScrollPrev}
        aria-label={`Previous ${label}`}
      />

      <div className="home-product-carousel__viewport" ref={viewportRef}>
        <div className="home-product-carousel__rail">
          {products.map((product) => (
            <ProductCard
              key={product.id || product._id}
              product={product}
              onAddToCart={onAddToCart}
              onAddToWishlist={onAddToWishlist}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        className="home-product-carousel__arrow home-product-carousel__arrow--next"
        onClick={() => scrollProducts(1)}
        disabled={!canScrollNext}
        aria-label={`Next ${label}`}
      />
    </div>
  );
};

const Home = () => {
  const [products, setProducts] = useState([]);
  const [carousels, setCarousels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const { addToCart, addToWishlist } = useCartWishlist();
  const { t } = useLanguage();
  const { settings } = useSiteSettings();
  const activeTestimonial = TESTIMONIALS[activeTestimonialIndex] || TESTIMONIALS[0];
  const mainPageSettings = settings?.mainPage || {};

  useEffect(() => {
    const load = async () => {
      try {
        const [productsData, carouselsData] = await Promise.all([
          productAPI.getAllAvailable(),
          carouselAPI.getAll()
        ]);
        setProducts(productsData);
        setCarousels(Array.isArray(carouselsData?.data) ? carouselsData.data : []);
      } catch (error) {
        console.error('Error loading home data:', error);
        setProducts([]);
        setCarousels([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const productsById = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const productId = getProductId(product);
      if (productId) {
        map.set(productId, normalizeProduct(product, product.category || 'product'));
      }
    });
    return map;
  }, [products]);

  const limitedOffers = useMemo(() => LIMITED_OFFERS.map((offer, index) => {
    const configuredOffer = mainPageSettings.limitedOffers?.find((item) => item?.id === offer.id) || mainPageSettings.limitedOffers?.[index] || {};
    const imageUrl = imageLooksUsable(configuredOffer.imageUrl) ? normalizeImageUrl(configuredOffer.imageUrl) : '';
    const hasConfiguredSale = Object.prototype.hasOwnProperty.call(configuredOffer, 'salePercentage');
    const salePercentage = Number(hasConfiguredSale ? configuredOffer.salePercentage : offer.salePercentage);

    return {
      ...offer,
      eyebrow: configuredOffer.eyebrow ?? null,
      title: configuredOffer.title || null,
      description: configuredOffer.description ?? null,
      href: buildPromoHref(configuredOffer.href || offer.href, Number.isFinite(salePercentage) && salePercentage > 0 ? salePercentage : 0),
      salePercentage: Number.isFinite(salePercentage) && salePercentage > 0 ? salePercentage : 0,
      imageUrl,
    };
  }), [mainPageSettings.limitedOffers]);

  const homeCarouselSlides = useMemo(() => {
    const adminSlides = carousels
      .filter((slide) => !slide.placement || slide.placement === 'brand-logo')
      .map(normalizeCarouselSlide)
      .filter((slide) => slide.title || slide.imageUrl);

    if (adminSlides.length) {
      return adminSlides;
    }

    return BRAND_LOGOS.map(normalizeCarouselSlide);
  }, [carousels]);

  const homeCarouselTrackSlides = useMemo(
    () => [...homeCarouselSlides, ...homeCarouselSlides],
    [homeCarouselSlides]
  );

  const curatedSections = useMemo(() => {
    return SECTION_CONFIG.map((section) => {
      const selectedIds = Array.isArray(mainPageSettings.categoryProductIds?.[section.key])
        ? mainPageSettings.categoryProductIds[section.key].filter(Boolean).slice(0, HOME_CATEGORY_PRODUCT_LIMIT)
        : [];
      const categoryProducts = products
        .map((product) => normalizeProduct(product, section.key))
        .filter((product) => {
          const value = String(product.category || '').toLowerCase();
          return value === section.key;
        });

      return {
        ...section,
        products: prioritizeHomepageProducts({
          selectedIds,
          categoryProducts,
          productsById,
        }),
      };
    });
  }, [mainPageSettings.categoryProductIds, products, productsById]);

  if (loading) return <Loader />;

  return (
    <div className="home-page">
      <HeroBanner carousels={carousels} />

      <div className="home-categories">
        {curatedSections.map((section, index) => (
          <React.Fragment key={section.key}>
            <section className="category-section">
              <div className="category-section__header">
                <h2>
                  <Link className="category-section__title-link" to={`/products?category=${encodeURIComponent(section.key)}`}>
                    {t(`category.${section.key}`)}
                  </Link>
                </h2>
                <BrandStrip brands={section.brands} label={`${t(`category.${section.key}`)} brands`} />
              </div>

              <CategoryProductCarousel
                products={section.products}
                label={t(`category.${section.key}`)}
                onAddToCart={addToCart}
                onAddToWishlist={addToWishlist}
              />
            </section>

            {index === 1 && (
              <>
                <section className="brand-logo-carousel" aria-label="Brand products">
                  <div
                    className="brand-logo-carousel__track"
                    style={{ '--brand-logo-slide-count': homeCarouselSlides.length }}
                  >
                    {homeCarouselTrackSlides.map((slide, slideIndex) => (
                      <div className="brand-logo-carousel__slide" key={`${slide.id}-${slideIndex}`}>
                        <div
                          className={`brand-logo-carousel__card brand-logo-carousel__card--${slide.style} ${slide.imageUrl ? 'brand-logo-carousel__card--has-image' : ''}`}
                          aria-label={`Open ${slide.title}`}
                        >
                          <Link className="brand-logo-carousel__main" to={slide.href} aria-label={`Open ${slide.title}`}>
                            {slide.imageUrl ? (
                              <img className="brand-logo-carousel__logo" src={slide.imageUrl} alt="" />
                            ) : (
                              <span className="brand-logo-carousel__mark" aria-hidden="true" />
                            )}
                            <span className="brand-logo-carousel__copy">
                              {slide.title && <span className="brand-logo-carousel__name">{slide.title}</span>}
                              {slide.subtitle && <span className="brand-logo-carousel__subtitle">{slide.subtitle}</span>}
                            </span>
                          </Link>
                          {slide.buttons.length > 0 && (
                            <span className="brand-logo-carousel__buttons">
                              {slide.buttons.map((button, buttonIndex) => (
                                <Link
                                  className="brand-logo-carousel__button"
                                  key={`${button.label}-${buttonIndex}`}
                                  to={button.href}
                                >
                                  {button.label}
                                </Link>
                              ))}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="limited-offers">
                  <h2 className="section-title">{mainPageSettings.limitedOffersTitle || t('home.limitedOffers')}</h2>
                  <div className="limited-offers__grid">
                    {limitedOffers.map((offer) => (
                      <Link
                        key={offer.id}
                        className={`promo-card promo-card--${offer.variant} ${offer.imageUrl ? 'promo-card--has-image' : ''}`}
                        style={offer.imageUrl ? { backgroundImage: `linear-gradient(180deg, rgba(80, 5, 119, 0.22), rgba(80, 5, 119, 0.52)), url("${offer.imageUrl}")` } : undefined}
                        to={offer.href}
                      >
                        {(offer.eyebrow !== null ? offer.eyebrow : (offer.eyebrowKey ? t(offer.eyebrowKey) : '')) && (
                          <span className="promo-card__eyebrow">
                            {offer.eyebrow !== null ? offer.eyebrow : t(offer.eyebrowKey)}
                          </span>
                        )}
                        <div className="promo-card__content">
                          {offer.salePercentage > 0 && (
                            offer.title && <span className="promo-card__discount">-{offer.salePercentage}%</span>
                          )}
                          <h3>
                            {splitLines(offer.title || t(offer.titleKey)).map((line) => (
                              <span key={line}>{line}</span>
                            ))}
                          </h3>
                          {(offer.description !== null ? offer.description : (offer.descriptionKey ? t(offer.descriptionKey) : '')) && (
                            <p>
                              {splitLines(offer.description !== null ? offer.description : t(offer.descriptionKey)).map((line) => (
                                <span key={line}>{line}</span>
                              ))}
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              </>
            )}

            {index === 3 && (
              <section className="home-testimonials">
                <div className="home-testimonials__inner">
                  <div className="testimonials-copy">
                    <h2>{t('home.testimonialsTitle')}</h2>
                    <div className="testimonial-dots" aria-label="Customer reviews">
                      {TESTIMONIALS.map((testimonial, testimonialIndex) => (
                        <button
                          key={testimonial.author}
                          type="button"
                          className={testimonialIndex === activeTestimonialIndex ? 'is-active' : ''}
                          onClick={() => setActiveTestimonialIndex(testimonialIndex)}
                          aria-label={`Show review from ${testimonial.author}`}
                          aria-pressed={testimonialIndex === activeTestimonialIndex}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="testimonials-stage">
                    <div className="testimonials-blob" aria-hidden="true" />
                    <div className="testimonials-star testimonials-star--one" aria-hidden="true" />
                    <div className="testimonials-star testimonials-star--two" aria-hidden="true" />
                    <article className="testimonials-card" aria-live="polite">
                      <p className="testimonials-card__quote">
                        {activeTestimonial.quote}
                      </p>
                      <div className="testimonials-card__rating" aria-label={`${activeTestimonial.rating} stars`}>
                        <span>{Array.from({ length: activeTestimonial.rating }, () => '★').join(' ')}</span>
                      </div>
                      <p className="testimonials-card__author">{activeTestimonial.author}</p>
                      <p className="testimonials-card__location">{activeTestimonial.location}</p>
                    </article>
                  </div>
                </div>
              </section>
            )}
          </React.Fragment>
        ))}
      </div>

      <section className="home-why">
        <h2>{t('home.whyTitle')}</h2>
        <div className="why-grid">
          {WHY_ITEMS.map((item) => (
            <article key={item.titleKey} className="why-card">
              <div className={`why-card__icon why-card__icon--${item.icon}`} aria-hidden="true" />
              <div>
                <h3>{t(item.titleKey)}</h3>
                <p>{t(item.textKey)}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
